import { parseISO } from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { webSocket } from 'rxjs/webSocket';

import { ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';

import { SnVideoPlayerComponent } from '@lib/player/video-player';

@Component({
  selector: 'sn-camera-ws-overlay',
  templateUrl: './camera-ws-overlay.component.html',
  styleUrls: ['./camera-ws-overlay.component.scss'],
})
export class CameraWsOverlayComponent implements OnInit, OnDestroy {
  @Input()
  set wsUrl(value: string) {
    if (value) {
      this._wsUrl = value;
      this._initSubscriptions();
    }
  }

  public objects: any[] = [];
  public storedObjects: any[] = [];

  private _wsUrl: string;
  private readonly _destroy$ = new Subject();

  constructor(
    @Inject('SN_PLAYER') private readonly _player: SnVideoPlayerComponent,
    private readonly _cd: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this.storedObjects = [];
    this._destroy$.next();
    this._destroy$.complete();
  }

  public getBoundings(): any {
    const rect = this._player.getBoundingVideoRect();
    if (rect.width) {
      return {
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
      };
    }
    return {};
  }

  private _initSubscriptions(): void {
    const url = this._wsUrl;
    const socket$ = webSocket({ url, protocol: 'tracker-protocol' });
    socket$.pipe(takeUntil(this._destroy$)).subscribe((data: any) => {
      if (data.Frame?.Objects) {
        this.storedObjects.push(
          ...data.Frame.Objects.map((o) => {
            const topLeft = o.Pos2D[0];
            const topRight = o.Pos2D[1];
            const bottomRight = o.Pos2D[2];
            const bottomLeft = o.Pos2D[3];
            const y = Math.min(Math.min(topLeft.y, topRight.y), 9999);
            const x = Math.min(Math.min(topLeft.x, bottomLeft.x), 9999);
            const width = Math.max(
              topRight.x - topLeft.x,
              bottomRight.x - bottomLeft.x,
              topRight.x - bottomLeft.x,
              bottomRight.x - topLeft.x,
            );
            const height = Math.max(
              bottomLeft.y - topLeft.y,
              bottomRight.y - topRight.y,
              bottomLeft.y - topRight.y,
              bottomRight.y - topLeft.y,
            );
            return {
              x: `${Math.max(x / 100, 0)}%`,
              y: `${Math.max(y / 100, 0)}%`,
              width: `${Math.max(width / 100, 0)}%`,
              height: `${Math.max(height / 100, 0)}%`,
              time: data.Frame.UtcTime,
              ...o,
            };
          }),
        );
      }
    });

    this._player.currentTime$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.objects = [];
      const ids = [...new Set(this.storedObjects.map((o) => o.Id))];
      ids.forEach((id) => {
        const mostRecentForId = this.storedObjects.find((object) => {
          const objectTime = parseISO(object.time);
          const diff = this._player?.reference?.actualTime - objectTime.getTime();
          return diff > -200 && diff < 200 && object.Id === id;
        });
        if (mostRecentForId) {
          this.objects.push(mostRecentForId);
        }
      });
      this.storedObjects = this.storedObjects.filter((object) => {
        const objectTime = parseISO(object.time);
        return this._player?.reference?.actualTime - objectTime.getTime() < 1000;
      });
      this._cd.detectChanges();
    });
  }
}
